// WatchPage.js — /watch, Scorigami Watch: the odds each fixture in the next
// Premier League round ends in a result the league has never seen (a
// home/away scoreline missing from the grid). The odds come from a goals
// model run by pl-gami-image every morning (lambdas/pl-gami-image/
// scorigami_model.py) and served by the API's ?watch=1; @pl_gami posts them
// before each round.

import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Skeleton, Typography } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, H2, InternalLink, P, PageHeader, ProsePage, Strong } from "./components/Prose";
import { BRAND } from "./theme";
import { useSeededQuery } from "./components/scorelines/data";
import { clubInfo, clubPath, seasonText } from "./components/clubs/data";
import { h2hPath } from "./components/clubs/h2h";

const SITE_URL = "https://premierleaguescorigami.com";

// "1 in 930": odds to two significant figures (as the bot writes them).
export function oddsText(p) {
  if (!p || p <= 0) return "no chance";
  const n = 1 / p;
  if (n < 10) return `1 in ${Number(n.toFixed(1))}`;
  const digits = Math.floor(Math.log10(n)) - 1;
  const rounded = Math.round(n / 10 ** digits) * 10 ** digits;
  return `1 in ${rounded.toLocaleString("en-GB")}`;
}
const pct = (p) => (p >= 0.1 ? `${Math.round(p * 100)}%` : `${(p * 100).toFixed(1)}%`);
const kickoff = (iso) =>
  new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
const longDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
const yearsAgo = (iso) => {
  const years = Math.floor((Date.now() - new Date(iso).getTime()) / (365.25 * 24 * 3600 * 1000));
  return years < 1 ? "less than a year ago" : years === 1 ? "a year ago" : `${years} years ago`;
};

export default function WatchPage() {
  const resp = useSeededQuery("watch=1");
  const data = resp.data;
  const fixtures = data?.fixtures || [];
  const title = data?.matchday && fixtures.length ? `Scorigami Watch: Matchweek ${data.matchday}` : "Scorigami Watch";

  return (
    <ProsePage wide>
      <SEO
        title="Scorigami Watch: this round's odds of a never-seen result"
        description="The odds each Premier League fixture this round ends in a result the league has never seen, from a goals model of recent results. Updated daily."
        path="/watch"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Scorigami Watch", item: `${SITE_URL}/watch` },
          ],
        }}
      />
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader
          eyebrow="Scorigami Watch"
          title={title}
          lede="The odds each game this round ends in a result the Premier League has never seen, from a goals model of recent form. Updated every morning."
        />
      </Box>

      {resp.error ? (
        <P>Scorigami Watch couldn't be loaded right now. Please try again shortly.</P>
      ) : !data ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={190} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 3 }} />
          ))}
        </Box>
      ) : fixtures.length ? (
        <Round data={data} />
      ) : (
        <Box sx={{ maxWidth: 820 }}>
          <P>
            There are no Premier League fixtures in the next four weeks, so there's nothing to watch yet. The odds for the next
            round appear here as soon as it's scheduled.
          </P>
          {data.lastNew && <LastNew lastNew={data.lastNew} />}
        </Box>
      )}

      <Box sx={{ maxWidth: 820 }}>
        <H2 id="how">How the odds are worked out</H2>
        <P>
          Every Premier League result since 1992 decides which scores have never happened. The last three seasons go into a
          goals model: each club gets an attacking and a defensive rating, fitted to its results with older matches counting
          for less (a match a year ago counts half as much as one today). Home sides get a boost, and a newly promoted club starts from a typical promoted side's ratings until its own
          results take over.
        </P>
        <P>
          The ratings give each side's expected goals for a fixture, and treating each side's goals as a Poisson count gives the
          chance of every possible score, from 0–0 up to 15–15. The odds of a never-seen result add up the chances of every score
          that has never happened with the same side at home.
        </P>
        <H2 id="never-seen">What counts as never seen?</H2>
        <P>
          A result counts as new when that exact home–away score has never happened, the empty squares on the{" "}
          <InternalLink to="/">home/away grid</InternalLink>. That includes a classic scorigami (a score never seen either way
          round) and a result that has only happened the other way round, such as the first away 2–7. The first-of-the-season
          figure is the chance of a result that hasn't happened yet this season.
        </P>
        <H2 id="accuracy">How accurate is it?</H2>
        <P>
          Tested on every match from 2016/17 to 2025/26, using only the results before each one, the model expected{" "}
          <Strong>3.3 never-seen results</Strong> across those ten seasons; there were 3. It expected 360 first-of-the-season
          results; there were 345. It can't know about injuries, red cards or weather, so treat a single fixture's odds as a guide
          rather than a forecast.
        </P>
        <H2 id="updates">When is it updated?</H2>
        <P>
          Every morning, from the latest fixtures and results. Before each round,{" "}
          <Box component="a" href="https://x.com/pl_gami" target="_blank" rel="noopener noreferrer" sx={{ color: BRAND.cyan, fontWeight: 600, textDecoration: "none" }}>
            @pl_gami
          </Box>{" "}
          posts the round's odds, and when a never-seen result happens it posts it within minutes of full time.
        </P>
      </Box>

      <Box sx={{ mt: 3 }}>
        <AdSlot position="records" />
      </Box>
      <CtaRow
        links={[
          { to: "/", label: "Explore the grid", primary: true },
          { to: "/scorelines", label: "Every scoreline" },
          { to: "/records", label: "Records" },
        ]}
      />
    </ProsePage>
  );
}

function Round({ data }) {
  const [order, setOrder] = useState("kickoff");
  const fixtures = useMemo(
    () => (order === "odds" ? [...data.fixtures].sort((a, b) => b.pNew - a.pNew) : data.fixtures),
    [data.fixtures, order]
  );
  const maxP = Math.max(...data.fixtures.map((f) => f.pNew));
  const likeliest = data.fixtures.reduce((a, b) => (b.pNew > a.pNew ? b : a));
  const newest = data.fixtures
    .filter((f) => f.newest)
    .reduce((a, b) => (!a || b.newest[2] > a.newest[2] ? b : a), null);
  const name = (team) => clubInfo(team).short;

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, mb: 3 }}>
        <Tile label="A never-seen result this round" value={oddsText(data.pAny)} accent />
        <Tile label="Likeliest game" value={oddsText(likeliest.pNew)} note={`${name(likeliest.home)} v ${name(likeliest.away)}`} />
        {newest && (
          <Tile
            label="Likeliest never-seen score"
            value={`${newest.newest[0]}–${newest.newest[1]}`}
            note={`${name(newest.home)} v ${name(newest.away)}, ${oddsText(newest.newest[2])}`}
          />
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Typography component="h2" sx={{ color: "#fff", fontSize: { xs: 19, sm: 21 }, fontWeight: 800, mr: "auto" }}>
          Matchweek {data.matchday}, {seasonText(data.season)}
        </Typography>
        <Box role="radiogroup" aria-label="Order" sx={{ display: "flex", gap: 0.5 }}>
          {[
            ["kickoff", "By kick-off"],
            ["odds", "By odds"],
          ].map(([v, label]) => (
            <Box
              key={v}
              component="button"
              type="button"
              role="radio"
              aria-checked={order === v}
              onClick={() => setOrder(v)}
              sx={{
                font: "inherit",
                cursor: "pointer",
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                fontSize: 12.5,
                fontWeight: 700,
                color: order === v ? "#fff" : BRAND.textSecondary,
                background: order === v ? "rgba(111,93,245,0.35)" : "transparent",
                border: `1px solid ${order === v ? "transparent" : "rgba(255,255,255,0.14)"}`,
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box component="ul" aria-label="Fixtures" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
        {fixtures.map((f) => (
          <li key={f.id}>
            <FixtureCard f={f} maxP={maxP} />
          </li>
        ))}
      </Box>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 12.5, mt: 1.5 }}>
        Kick-off times are in your time zone. Updated {new Date(data.updatedAt).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}.
      </Typography>

      {data.lastNew && (
        <Box sx={{ maxWidth: 820, mt: 3 }}>
          <LastNew lastNew={data.lastNew} />
        </Box>
      )}
    </>
  );
}

function Tile({ label, value, note, accent }) {
  return (
    <Box sx={{ p: 2, borderRadius: 3, background: accent ? "rgba(111,93,245,0.16)" : "rgba(255,255,255,0.035)", border: `1px solid ${accent ? "rgba(111,93,245,0.45)" : "rgba(255,255,255,0.08)"}` }}>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 11.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ color: accent ? BRAND.cyan : "#fff", fontSize: 26, fontWeight: 900, mt: 0.5, fontFeatureSettings: '"tnum"' }}>{value}</Typography>
      {note && <Typography sx={{ color: "#c9ccd6", fontSize: 13, mt: 0.25 }}>{note}</Typography>}
    </Box>
  );
}

function Team({ team, crest, align }) {
  const info = clubInfo(team);
  return (
    <Box
      component={RouterLink}
      to={clubPath(team)}
      sx={{ display: "flex", flexDirection: align === "right" ? "row-reverse" : "row", alignItems: "center", gap: 1, minWidth: 0, flex: 1, textDecoration: "none", color: "#fff", "&:hover span": { color: BRAND.cyan } }}
    >
      {crest && <Box component="img" src={crest} alt="" loading="lazy" sx={{ width: 30, height: 30, objectFit: "contain", flexShrink: 0 }} />}
      <Box component="span" sx={{ fontSize: 15.5, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: align }}>
        {info.short}
      </Box>
    </Box>
  );
}

function FixtureCard({ f, maxP }) {
  const [likelyH, likelyA, likelyP] = f.likely[0];
  return (
    <Box
      sx={{
        height: "100%",
        boxSizing: "border-box",
        p: { xs: 1.75, sm: 2 },
        borderRadius: 3,
        background: "linear-gradient(180deg, rgba(25,25,32,0.85) 0%, rgba(18,18,24,0.85) 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 12, fontWeight: 700, mb: 1 }}>{kickoff(f.utcDate)}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Team team={f.home} crest={f.homeCrest} />
        <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>v</Typography>
        <Team team={f.away} crest={f.awayCrest} align="right" />
      </Box>

      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mt: 1.75 }}>
        <Typography sx={{ color: "#c9ccd6", fontSize: 13 }}>Never-seen result</Typography>
        <Typography sx={{ color: "#fff", fontSize: 22, fontWeight: 900, fontFeatureSettings: '"tnum"' }}>{oddsText(f.pNew)}</Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,0.08)", mt: 0.75, overflow: "hidden" }} aria-hidden>
        <Box sx={{ height: "100%", width: `${Math.max(3, (100 * f.pNew) / maxP)}%`, borderRadius: 4, background: BRAND.gradient }} />
      </Box>

      <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: 2, rowGap: 0.5, m: 0, mt: 1.5, fontSize: 13, "& dt": { color: BRAND.textSecondary }, "& dd": { m: 0, color: "#e6e8ee", fontWeight: 700, textAlign: "right", fontFeatureSettings: '"tnum"' } }}>
        <dt>Most likely score</dt>
        <dd>
          {likelyH}–{likelyA} ({pct(likelyP)})
        </dd>
        <dt>Expected goals</dt>
        <dd>
          {f.xg[0].toFixed(1)} – {f.xg[1].toFixed(1)}
        </dd>
        <dt>A result not yet seen this season</dt>
        <dd>{pct(f.pSeasonNew)}</dd>
        {f.newest && (
          <>
            <dt>Likeliest never-seen score</dt>
            <dd>
              {f.newest[0]}–{f.newest[1]} ({oddsText(f.newest[2])})
            </dd>
          </>
        )}
      </Box>
      <Box
        component={RouterLink}
        to={h2hPath(f.home, f.away)}
        sx={{ display: "inline-block", mt: 1.5, color: BRAND.cyan, fontSize: 13.5, fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
      >
        {clubInfo(f.home).short} v {clubInfo(f.away).short}: head to head →
      </Box>
    </Box>
  );
}

function LastNew({ lastNew }) {
  const h = clubInfo(lastNew.home).short;
  const a = clubInfo(lastNew.away).short;
  return (
    <P>
      The last time the Premier League produced a result it had never seen was {h} {lastNew.homeScore}–{lastNew.awayScore} {a} on{" "}
      {longDate(lastNew.utcDate)}, {yearsAgo(lastNew.utcDate)}.
    </P>
  );
}
